import React from 'react';
import { useNavigate } from 'react-router-dom';
import { useThemeMode } from '../../ThemeContext';

const DocumentStorageOverview = ({ dashboardData }) => {
  const { mode } = useThemeMode();
  const navigate = useNavigate();

  // Extract storage data from API response
  const storageOverview = dashboardData?.document_storage_overview || {};
  const totalUploadedSizeMB = storageOverview.total_uploaded_size_mb || 0;
  const allocatedSizeMB = storageOverview.allocated_size_mb || 0;
  const usagePercentage = storageOverview.usage_percentage || 0;

  // Debug logs to check data
  console.log('DocumentStorageOverview - storageOverview:', storageOverview);
  console.log('DocumentStorageOverview - totalUploadedSizeMB:', totalUploadedSizeMB);
  console.log('DocumentStorageOverview - allocatedSizeMB:', allocatedSizeMB);
  console.log('DocumentStorageOverview - usagePercentage:', usagePercentage);

  // Calculate storage values
  const usedStorage = parseFloat(totalUploadedSizeMB.toFixed(2)); // 0.14 MB
  const totalStorageMB = parseFloat(allocatedSizeMB.toFixed(2)); // 10240 MB
  const leftStorage = Math.max(0, totalStorageMB - usedStorage); // 10239.86 MB
  
  // Convert total storage to GB for center display
  const totalStorageGB = (totalStorageMB / 1024).toFixed(1); // 10.0 GB

  // Use the usage_percentage directly from API for the progress arc
  const progressAngle = (usagePercentage / 100) * 180;

  return (
    <div 
      onClick={() => navigate('/documents')}
      style={{
        backgroundColor: mode === 'dark' ? '#1a1a1a' : '#ffffff',
        borderRadius: '12px',
        padding: '24px',
        border: mode === 'dark' ? '1px solid #333' : '1px solid #e0e0e0',
        height: '280px',
        overflow: 'hidden',
        cursor: 'pointer',
        transition: 'transform 0.2s ease, box-shadow 0.2s ease'
      }}
      onMouseEnter={(e) => {
        e.currentTarget.style.transform = 'translateY(-2px)';
        e.currentTarget.style.boxShadow = mode === 'dark' 
          ? '0 4px 12px rgba(0, 0, 0, 0.5)' 
          : '0 4px 12px rgba(0, 0, 0, 0.1)';
      }}
      onMouseLeave={(e) => {
        e.currentTarget.style.transform = 'translateY(0)';
        e.currentTarget.style.boxShadow = 'none';
      }}
    >
      {/* Header */}
      <h3 style={{
        fontSize: '18px',
        fontWeight: '600',
        margin: '0 0 24px 0',
        color: mode === 'dark' ? '#ffffff' : '#333333',
        fontFamily: 'Urbanist, Arial, sans-serif'
      }}>
        Document Storage Overview
      </h3>

      {/* Semi-Circle Progress Container */}
      <div style={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        gap: '20px',
        height: '200px'
      }}>
        {/* Semi-Circle Progress Bar */}
        <div style={{ 
          position: 'relative', 
          width: '200px', 
          height: '120px',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center'
        }}>
          {/* Background Arc */}
          <svg width="200" height="120" style={{ position: 'absolute' }}>
            <path
              d="M 20 100 A 80 80 0 0 1 180 100"
              fill="none"
              stroke={mode === 'dark' ? '#333333' : '#e0e0e0'}
              strokeWidth="20"
              strokeLinecap="round"
            />
            {/* Progress Arc */}
            <path
              d="M 20 100 A 80 80 0 0 1 180 100"
              fill="none"
              stroke="#26c3c0"
              strokeWidth="20"
              strokeLinecap="round"
              strokeDasharray={`${progressAngle * 3.14159 * 80 / 180} 502`}
              strokeDashoffset="0"
              style={{
                transition: 'stroke-dasharray 0.3s ease'
              }}
            />
          </svg>
          
          {/* Center Text */}
          <div style={{
            textAlign: 'center',
            zIndex: 1,
            marginTop: '15px'
          }}>
            <div style={{
              fontSize: '24px',
              fontWeight: '700',
              color: mode === 'dark' ? '#ffffff' : '#333333',
              fontFamily: 'Urbanist, Arial, sans-serif',
              lineHeight: '1'
            }}>
              {totalStorageGB} GB
            </div>
            <div style={{
              fontSize: '12px',
              color: mode === 'dark' ? '#888888' : '#666666',
              fontFamily: 'Urbanist, Arial, sans-serif',
              marginTop: '4px'
            }}>
              Total
            </div>
          </div>
        </div>

        {/* Storage Info */}
        <div style={{
          display: 'flex',
          gap: '32px',
          width: '100%',
          justifyContent: 'center'
        }}>
          <div style={{
            display: 'flex',
            alignItems: 'center',
            gap: '8px'
          }}>
            <div style={{
              width: '8px',
              height: '8px',
              borderRadius: '50%',
              backgroundColor: '#26c3c0'
            }}></div>
            <div>
              <div style={{
                fontSize: '12px',
                fontWeight: '600',
                color: mode === 'dark' ? '#ffffff' : '#333333',
                fontFamily: 'Urbanist, Arial, sans-serif'
              }}>
                Used Storage
              </div>
              <div style={{
                fontSize: '11px',
                color: mode === 'dark' ? '#888888' : '#666666',
                fontFamily: 'Urbanist, Arial, sans-serif'
              }}>
                {usedStorage} MB
              </div>
            </div>
          </div>

          <div style={{
            display: 'flex',
            alignItems: 'center',
            gap: '8px'
          }}>
            <div style={{
              width: '8px',
              height: '8px',
              borderRadius: '50%',
              backgroundColor: mode === 'dark' ? '#333333' : '#e0e0e0'
            }}></div>
            <div>
              <div style={{
                fontSize: '12px',
                fontWeight: '600',
                color: mode === 'dark' ? '#ffffff' : '#333333',
                fontFamily: 'Urbanist, Arial, sans-serif'
              }}>
                Left Storage
              </div>
              <div style={{
                fontSize: '11px',
                color: mode === 'dark' ? '#888888' : '#666666',
                fontFamily: 'Urbanist, Arial, sans-serif'
              }}>
                {leftStorage} MB
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default DocumentStorageOverview;
